import React from 'react';
import { Box, DialogActions, DialogContent, DialogTitle, Drawer, DrawerProps, IconButton, Theme } from '@mui/material';
import { Close } from '@mui/icons-material';

type DrawerDialogWidth = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface SideDrawerDialogProps extends Omit<DrawerProps, 'anchor' | 'variant'> {
  maxWidth?: DrawerDialogWidth | false;
  fullWidth?: boolean;
  fullScreen?: boolean;
}

const widthByMaxWidth: Record<DrawerDialogWidth, number> = {
  xs: 420,
  sm: 560,
  md: 760,
  lg: 980,
  xl: 1180,
};

const drawerSubtitles: Record<string, string> = {
  'Switch Context': 'Choose the organisation and practice context for your workspace.',
  'Insert Image': 'Add an image by URL or upload one from your device.',
  'Add Source': 'Attach text or links that should guide the generated output.',
  'Prompt Library': 'Create, edit, and reuse saved prompts for faster drafting.',
  'Source Library': 'Manage reusable sources and add them to the current session.',
  'Add New Category': 'Create a document category for organising knowledge content.',
  'Save New Version': 'Capture the current document content as a version snapshot.',
  'Read Compliance': 'Review who has opened and confirmed the current document version.',
  'Standing Order Acknowledgements': 'Track staff acknowledgement and training confirmations.',
  'Acknowledge Standing Order': 'Confirm your training and obligations for this standing order.',
  'Rename Item': 'Update the item name without changing its content or location.',
  'Create Folder': 'Add a folder to organise resource files and approved links.',
  'Upload Resource': 'Attach resource files and publish them to the selected folder.',
  'Add Approved Link': 'Create a reviewed external link for staff reference.',
  'Edit Approved Link': 'Update link details, approval status, and tags.',
  'Create Quiz': 'Build quiz questions, answers, and pass criteria.',
  'Edit Quiz': 'Update quiz details, questions, and answer options.',
  'Assignments': 'Review staff quiz assignments and completion status.',
  'Quiz Responses': 'Inspect submitted answers, scores, and completion outcomes.',
  'Generate Quiz with AI': 'Create draft questions from a published document.',
  'Edit Lookup Type': 'Update the lookup group name and description.',
  'Add Lookup Type': 'Create a configurable lookup group for reusable values.',
  'Edit Value': 'Update this lookup value and display order.',
  'Add Value': 'Add a reusable option to the selected lookup group.',
  'Escalate Complaint': 'Record escalation details and external agency context.',
  'Export Complaint Report': 'Choose report sections before generating the PDF.',
};

const extractTitleText = (value: React.ReactNode): string => {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(extractTitleText).join(' ').trim();
  if (React.isValidElement(value)) return extractTitleText((value.props as any).children);
  return '';
};

const containsCloseIcon = (value: React.ReactNode): boolean => {
  if (Array.isArray(value)) return value.some(containsCloseIcon);
  if (!React.isValidElement(value)) return false;

  if (value.type === Close) {
    return true;
  }

  const props = value.props as any;
  const ariaLabel = String(props['aria-label'] || '').toLowerCase();
  if (ariaLabel.includes('close')) {
    return true;
  }

  return containsCloseIcon(props.children);
};

const getDrawerSubtitle = (titleText: string) => {
  if (drawerSubtitles[titleText]) return drawerSubtitles[titleText];
  if (titleText.startsWith('Assignments')) return drawerSubtitles.Assignments;
  if (titleText.startsWith('Quiz Responses')) return drawerSubtitles['Quiz Responses'];
  if (titleText.includes('Preview')) return 'Preview saved content before returning to the current document.';
  if (titleText.includes('Compare')) return 'Compare saved content against the current document version.';
  return '';
};

const SideDrawerDialog: React.FC<SideDrawerDialogProps> = ({
  maxWidth = 'sm',
  fullWidth,
  fullScreen,
  PaperProps,
  children,
  ...props
}) => {
  const drawerWidth = fullScreen
    ? '100vw'
    : typeof maxWidth === 'string'
      ? widthByMaxWidth[maxWidth]
      : 640;

  const enhanceChildren = () => React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child;

    const childType = child.type as any;
    const isDialogTitle = child.type === DialogTitle || childType?.muiName === 'MuiDialogTitle';
    const isDialogContent = child.type === DialogContent || childType?.muiName === 'MuiDialogContent';
    const isDialogActions = child.type === DialogActions || childType?.muiName === 'MuiDialogActions';

    if (isDialogTitle) {
      const existingSx = (child.props as any).sx || {};
      const originalChildren = (child.props as any).children;
      const titleText = extractTitleText(originalChildren).replace(/\s+/g, ' ').trim();
      const subtitle = typeof originalChildren === 'string' ? getDrawerSubtitle(titleText) : '';
      const titleAlreadyHasClose = containsCloseIcon(originalChildren);
      return React.cloneElement(child as React.ReactElement<any>, {
        sx: {
          ...existingSx,
          px: 2.5,
          py: 1.55,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minHeight: 56,
          boxShadow: (theme: Theme) => theme.palette.mode === 'dark'
            ? '0 1px 0 rgba(255,255,255,0.02)'
            : '0 1px 0 rgba(15, 23, 42, 0.02)',
        },
        children: (
          <>
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
                color: 'text.primary',
                fontSize: '0.95rem',
                fontWeight: 900,
                lineHeight: 1.3,
                '& .MuiTypography-root': {
                  color: 'text.primary',
                  fontSize: '0.95rem',
                  fontWeight: 900,
                  lineHeight: 1.3,
                },
                '& .MuiTypography-body2, & .MuiTypography-caption': {
                  mt: 0.35,
                  fontSize: '0.72rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                },
              }}
            >
              {originalChildren}
              {subtitle && (
                <Box component="span" sx={{ display: 'block', mt: 0.35, fontSize: '0.72rem', fontWeight: 500, color: 'text.secondary' }}>
                  {subtitle}
                </Box>
              )}
            </Box>
            {props.onClose && !titleAlreadyHasClose && (
              <IconButton
                size="small"
                aria-label="Close drawer"
                onClick={(event) => props.onClose?.(event, 'escapeKeyDown')}
                sx={{
                  ml: 'auto',
                  width: 30,
                  height: 30,
                  borderRadius: 1,
                  color: 'text.secondary',
                  border: '1px solid',
                  borderColor: 'transparent',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: (theme: Theme) => theme.palette.mode === 'dark' ? '#13243a' : '#eef5ff',
                    borderColor: (theme: Theme) => theme.palette.mode === 'dark' ? '#28435f' : '#d6e5ff',
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            )}
          </>
        ),
      });
    }

    if (isDialogContent) {
      const existingSx = (child.props as any).sx || {};
      return React.cloneElement(child as React.ReactElement<any>, {
        sx: {
          px: 2.5,
          pt: 1.25,
          pb: 3,
          flex: 1,
          overflowY: 'auto',
          bgcolor: 'background.default',
          '& .MuiOutlinedInput-root, & .MuiInputBase-root': {
            bgcolor: 'background.paper',
          },
          '& .MuiCard-root, & .MuiPaper-root': {
            borderRadius: 1,
          },
          ...existingSx,
        },
      });
    }

    if (isDialogActions) {
      const existingSx = (child.props as any).sx || {};
      return React.cloneElement(child as React.ReactElement<any>, {
        sx: {
          ...existingSx,
          px: 2.5,
          py: 1.55,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          gap: 1,
          '& .MuiButton-root': {
            minHeight: 34,
            borderRadius: 1,
            fontWeight: 800,
            boxShadow: 'none',
          },
        },
      });
    }

    return child;
  });

  return (
    <Drawer
      anchor="right"
      {...props}
      sx={{
        ...(props.sx || {}),
        '& .MuiDrawer-paper': {
          top: 0,
          bottom: 0,
          height: '100dvh',
          minHeight: '100dvh',
          maxHeight: '100dvh',
        },
      }}
      PaperProps={{
        ...PaperProps,
        sx: {
          width: { xs: '100vw', sm: fullWidth ? drawerWidth : Math.min(Number(drawerWidth) || 640, 640), md: drawerWidth },
          maxWidth: '100vw',
          top: 0,
          bottom: 0,
          height: '100dvh',
          minHeight: '100dvh',
          maxHeight: '100dvh',
          bgcolor: 'background.default',
          borderLeft: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: (theme: Theme) => theme.palette.mode === 'dark'
            ? '-24px 0 48px rgba(0,0,0,0.36)'
            : '-24px 0 48px rgba(15, 23, 42, 0.16)',
          '& .MuiDialogTitle-root + .MuiDialogContent-root': {
            borderTop: 0,
          },
          ...(PaperProps?.sx || {}),
        },
      }}
    >
      {enhanceChildren()}
    </Drawer>
  );
};

export default SideDrawerDialog;
